/**
 * Typed BroadcastChannel bus between the owner tab and follower tabs.
 *
 *  - Owner → all:        { kind: 'snapshot', snapshot }    (full mirror of phone state)
 *  - Follower → owner:   { kind: 'command', cmd, payload } (proxy a user action)
 *  - New follower → owner:{ kind: 'request-snapshot' }      (immediate first sync)
 *
 * Full snapshots (not patches): there are only ever 0–2 live calls, so a whole
 * serialisable snapshot per change is cheap and removes any patch-reconciliation
 * bugs. Dates are serialised as ISO strings (see `phone-state.ts`).
 */

import type {
  Brand,
  CallDirection,
  CallState,
  CallerInfo,
  PhoneStatus,
  TransferStateInfo,
  DialContext,
} from './types';

export interface SerializableCall {
  clientCallId: string;
  callId: string | null;
  telnyxCallId: string | null;
  conversationId: string | null;
  direction: CallDirection;
  state: CallState;
  brand: Brand;
  remoteNumber: string;
  startedAt: string; // ISO
  answeredAt: string | null; // ISO
  endedAt: string | null; // ISO
  customer: CallerInfo | null;
  recoveredFromCallId: string | null;
  holdSince: string | null; // ISO
  transferState: TransferStateInfo | null;
}

export interface SerializableStats {
  packetLossPct: number;
  jitterMs: number;
  rttMs: number;
  quality: 'good' | 'fair' | 'poor';
}

export interface CallError {
  clientCallId: string;
  message: string;
}

export interface PhoneSnapshot {
  calls: SerializableCall[];
  status: PhoneStatus;
  muted: boolean;
  disposition: SerializableCall | null;
  /** Quality of the current active call, sampled on the owner. */
  stats: SerializableStats | null;
  /** Laatste call-fout (bv. aannemen mislukt) — zichtbaar op elke tab. */
  lastError: CallError | null;
  /** Melding buiten een gesprek (bv. verbinding stil weggevallen en hersteld). */
  notice?: string | null;
}

export type PhoneCommand =
  | { cmd: 'answer'; payload: { clientCallId: string } }
  | { cmd: 'reject'; payload: { clientCallId: string } }
  | { cmd: 'hangup'; payload: { clientCallId: string } }
  | { cmd: 'mute-toggle'; payload: { clientCallId: string } }
  | { cmd: 'dtmf'; payload: { clientCallId: string; digit: string } }
  | { cmd: 'dial'; payload: { number: string; brand: string; customer: CallerInfo | null; context?: DialContext | null } }
  | { cmd: 'clear-disposition'; payload: Record<string, never> }
  | {
      cmd: 'set-devices';
      payload: {
        microphoneId?: string | null;
        microphoneLabel?: string | null;
        speakerId?: string | null;
        speakerLabel?: string | null;
        ringtoneVolume?: number;
      };
    };

export type BusMessage =
  | { kind: 'snapshot'; snapshot: PhoneSnapshot }
  | ({ kind: 'command' } & PhoneCommand)
  | { kind: 'request-snapshot' }
  /** Eigen presence gewijzigd (afwezig/beschikbaar) — elke tab neemt 'm over. */
  | { kind: 'presence'; presence: 'available' | 'dnd' };

type BusListener = (msg: BusMessage) => void;

const CHANNEL = 'cinco-phone-bus';

let channel: BroadcastChannel | null = null;
const listeners = new Set<BusListener>();

function ensureChannel(): BroadcastChannel | null {
  if (channel) return channel;
  if (typeof BroadcastChannel === 'undefined') return null;
  channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = (event) => {
    const msg = event.data as BusMessage;
    listeners.forEach((l) => {
      try {
        l(msg);
      } catch (err) {
        console.error('[telephony/phone-bus] listener error', err);
      }
    });
  };
  return channel;
}

function post(msg: BusMessage): void {
  const ch = ensureChannel();
  try {
    ch?.postMessage(msg);
  } catch (err) {
    console.error('[telephony/phone-bus] post failed', err);
  }
}

export function postSnapshot(snapshot: PhoneSnapshot): void {
  post({ kind: 'snapshot', snapshot });
}

export function postCommand(command: PhoneCommand): void {
  post({ kind: 'command', ...command });
}

export function requestSnapshot(): void {
  post({ kind: 'request-snapshot' });
}

export function postPresence(presence: 'available' | 'dnd'): void {
  post({ kind: 'presence', presence });
}

export function onMessage(cb: BusListener): () => void {
  ensureChannel();
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function closeBus(): void {
  try {
    channel?.close();
  } catch {
    /* ignore */
  }
  channel = null;
  listeners.clear();
}
